import type { Context, Provider } from 'react';
import { createContext } from 'react';

export interface LeftNavResizeContextValue {
  /**
   * Committed left-nav width supplied by the resizable left-nav experiment, or `null` when the nav is
   * collapsed or the experiment is inactive. When set, `LeftNavContainer` drives its width from this
   * value (via the `leftNavWidthCssVariable` fallback) instead of the responsive breakpoint width.
   */
  readonly overrideWidth: number | null;
  /**
   * Enables the animated left-nav width transition. Defaults to `false` so responsive/forced-collapse
   * width changes snap for users outside the resizable left-nav experiment, and stays `false` while a
   * pointer resize is in progress so the column tracks the pointer without lag. The experiment opts in
   * for its idle and collapse-toggle frames.
   */
  readonly isWidthTransitionActive: boolean;
}

const defaultValue: LeftNavResizeContextValue = { overrideWidth: null, isWidthTransitionActive: false };

export const LeftNavResizeContext: Context<LeftNavResizeContextValue> =
  createContext<LeftNavResizeContextValue>(defaultValue);

export const LeftNavResizeProvider: Provider<LeftNavResizeContextValue> = LeftNavResizeContext.Provider;

/**
 * CSS custom property that carries the live left-nav width during a pointer resize. The width is
 * written imperatively to this variable each animation frame so the column tracks the pointer
 * without re-rendering React; the committed React width is used as the fallback and once the drag
 * ends.
 */
export const leftNavWidthCssVariable = '--engage-left-nav-width';
